import { useEffect } from 'react';
import { Checkbox } from '@components/common/Checkbox';
import { BoxItemList, FilterItem } from '@components/filter/FilterStyle';
import theme from '@theme/index';
import styled from 'styled-components';

/**
 * CheckboxGroupFilter - 체크박스 그룹 필터 컴포넌트
 *
 * 복수 선택이 가능한 체크박스 그룹 필터
 * 기존 Checkbox 공용 컴포넌트를 활용
 *
 * @param title - 필터 타이틀
 * @param options - 체크박스 옵션 목록 { key, title }
 * @param selected - 선택된 키 배열
 * @param setSelected - 선택 상태 변경 함수
 * @param initialValue - 초기값 (필터 초기화 시 사용)
 * @param description - 필터 설명
 */

interface Option {
  key: string;
  title: string;
}

interface Props {
  title: string;
  options: Option[];
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  initialValue?: string[];
  description?: string;
}

const CheckboxGroupFilter = ({
  title,
  options,
  selected,
  setSelected,
  initialValue,
  description,
}: Props) => {
  const allKeys = options.map((option) => option.key);
  const isAllSelected = allKeys.every((key) => selected.includes(key));

  const handleChange = (key: string) => {
    if (selected.includes(key)) {
      setSelected(selected.filter((item) => item !== key));
    } else {
      setSelected([...selected, key]);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(allKeys);
    }
  };

  const handleReset = () => {
    if (initialValue) {
      setSelected(initialValue);
    }
  };

  useEffect(() => {
    window.addEventListener('reset-filter', handleReset);
    return () => window.removeEventListener('reset-filter', handleReset);
  });

  return (
    <FilterItem
      title={title}
      description={description}
      content={
        <CheckboxList>
          <CheckboxItem onClick={handleSelectAll}>
            <input
              type="checkbox"
              checked={isAllSelected}
              readOnly
              onClick={(e) => e.stopPropagation()}
            />
            <span className="checkbox_icon" />
            <p>전체</p>
          </CheckboxItem>
          {options.map((option) => (
            <CheckboxItem key={option.key} onClick={() => handleChange(option.key)}>
              <input
                type="checkbox"
                checked={selected.includes(option.key)}
                readOnly
                onClick={(e) => e.stopPropagation()}
              />
              <span className="checkbox_icon" />
              <p>{option.title}</p>
            </CheckboxItem>
          ))}
        </CheckboxList>
      }
    />
  );
};

export default CheckboxGroupFilter;

const CheckboxList = styled(BoxItemList)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
`;

const CheckboxItem = styled(Checkbox)`
  cursor: pointer;
  color: ${theme.neutralGray[600]};
  margin-bottom: 0;
  display: flex;
  align-items: center;

  p {
    font-size: 0.875rem;
    user-select: none;
    margin: 0 0 0 0.5rem;
  }
`;
